import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { track } from "../api";

const Ctx = createContext(null);
export const useConnectivity = () => useContext(Ctx);

const MAX_BACKOFF = 60000;

export function ConnectivityProvider({ children }) {
  const [status, setStatus] = useState("online"); // online | reconnecting | offline
  const [lastConnected, setLastConnected] = useState(null);
  const backoff = useRef(3000);
  const timer = useRef(null);
  const expeditionActive = useRef(false);

  const ping = useCallback(async () => {
    try {
      const r = await api.get("/health", { timeout: 6000 });
      return !!r?.data?.ok;
    } catch (e) {
      return false;
    }
  }, []);

  const goOnline = useCallback((wasOffline) => {
    setStatus("online");
    setLastConnected(new Date().toISOString());
    backoff.current = 3000;
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (wasOffline) track("connection_restored");
  }, []);

  const goOffline = useCallback(() => {
    setStatus((prev) => {
      if (prev !== "offline") track("connection_lost");
      return "offline";
    });
  }, []);

  // exponential-backoff background probe while not online
  const scheduleProbe = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const ok = await ping();
      if (ok) { goOnline(true); }
      else { backoff.current = Math.min(backoff.current * 2, MAX_BACKOFF); scheduleProbe(); }
    }, backoff.current);
  }, [ping, goOnline]);

  useEffect(() => {
    if (status === "offline" || status === "reconnecting") scheduleProbe();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [status, scheduleProbe]);

  // initial + event-driven checks (navigator.onLine is only a hint)
  useEffect(() => {
    let mounted = true;
    (async () => {
      const ok = await ping();
      if (mounted) { ok ? goOnline(false) : goOffline(); }
    })();
    const onOffline = () => goOffline();
    const onOnline = async () => {
      setStatus("reconnecting");
      const ok = await ping();
      ok ? goOnline(true) : goOffline();
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      mounted = false;
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [ping, goOnline, goOffline]);

  // manual retry: verify server, then hard-reload so user + active session are re-fetched
  const retry = useCallback(async () => {
    track("retry_clicked");
    setStatus("reconnecting");
    const ok = await ping();
    if (ok) {
      track("connection_restored");
      window.location.reload();
      return true;
    }
    goOffline();
    return false;
  }, [ping, goOffline]);

  const setExpeditionActive = useCallback((v) => { expeditionActive.current = v; }, []);
  const isExpeditionActive = useCallback(() => expeditionActive.current, []);

  return (
    <Ctx.Provider value={{ status, lastConnected, retry, ping, setExpeditionActive, isExpeditionActive }}>
      {children}
    </Ctx.Provider>
  );
}
